import React from 'react';
import { isChunkError, reloadOnceForChunk, alreadyTriedReload } from 'utils/chunkReload';

/**
 * A render error used to unmount the whole SPA into a silent black page —
 * no message, no way to know what broke, "empty page" bug reports. This
 * boundary shows the actual error and a reload button instead, so the person
 * hitting it can read (and report) the real cause.
 *
 * One error is handled rather than reported: a chunk that 404s after a deploy
 * is not a bug the person can act on, so it reloads itself. React does not
 * re-throw to window.onerror once a boundary has handled an error, which is
 * why the listeners in index.js never saw these and the coach was left
 * clicking Reload by hand.
 */
export default class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, healing: false };
  }

  static getDerivedStateFromError(error) {
    // Only claim to be healing if a reload has not already been tried — a
    // second failure means reloading is not the answer, so show the error.
    return { error, healing: isChunkError(error) && !alreadyTriedReload() };
  }

  componentDidCatch(error, info) {
    if (isChunkError(error)) {
      if (reloadOnceForChunk(error)) return;
      // Already reloaded once and it still fails: stop, and show the error.
      this.setState({ healing: false });
    }
    // eslint-disable-next-line no-console
    console.error('[AppErrorBoundary]', error, info?.componentStack);
  }

  render() {
    const { error, healing } = this.state;
    if (!error) return this.props.children;

    if (healing) {
      return (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#14121f', color: '#fff', fontFamily: 'inherit', padding: 24 }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>↻</div>
            <h2 style={{ margin: '0 0 8px', fontSize: 20 }}>Updating to the latest version</h2>
            <p style={{ color: '#b7b1c9', fontSize: 14, margin: 0 }}>One moment — this page will reload itself.</p>
          </div>
        </div>
      );
    }
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#14121f', color: '#fff', fontFamily: 'inherit', padding: 24 }}>
        <div style={{ maxWidth: 640, textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>⚠️</div>
          <h2 style={{ margin: '0 0 8px' }}>Something went wrong on this page</h2>
          <p style={{ color: '#b7b1c9', fontSize: 14, lineHeight: 1.6 }}>
            Send this to support / the developer:
          </p>
          <pre
            style={{
              textAlign: 'left',
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: 10,
              padding: 14,
              fontSize: 12,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              maxHeight: 240,
              overflow: 'auto'
            }}
          >
            {String(error?.message || error)}
          </pre>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              marginTop: 16,
              background: '#7C5BE0',
              color: '#fff',
              border: 'none',
              borderRadius: 10,
              padding: '10px 24px',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            Reload the page
          </button>
        </div>
      </div>
    );
  }
}
